/**
 * Pure builders for the **Meridian-owned** annotation render layers (ADR-034
 * design update PR D — the full Meridian render path). Terra Draw now only
 * *draws*; persisted annotations are displayed by MapLibre layers
 * (:file:`@/map/annotation-render-layer.tsx`) so we control everything,
 * including the per-shape **dash** style that Terra Draw's RenderMode can't do.
 *
 * Four sources:
 *   - **fill** — circle + polygon as ``Polygon`` (data-driven fill colour/opacity).
 *   - **outline** — line + circle/polygon rings as ``LineString`` (data-driven
 *     colour/width; one layer per dash pattern since MapLibre ``line-dasharray``
 *     isn't data-driven).
 *   - **marker** — markers as ``Point`` (data-driven colour/radius).
 *   - **arrow** — line-end arrowheads as ground-geometry ``Polygon`` triangles
 *     (painted by a fill layer; a fixed size baked at draw time that then scales
 *     with zoom — ADR-034).
 *
 * Every feature carries ``annotation_id`` (the map-click selection reads it).
 * The selection highlight (brighter, thicker) is resolved into the outline +
 * marker feature props; the source is rebuilt when the selection changes.
 *
 * Pure (no map / no GL) so it's CI-testable; the layers are verified in the
 * deferred §5.11 visual pass.
 *
 * Cross-references:
 *   - ADR-034 design update; feature-spec §5.2 / §10.
 *   - @/annotations/annotation-circle-geometry (circleRing — shared geometry).
 */
import type { Feature, FeatureCollection, LineString, Point, Polygon } from "geojson";
import type { Annotation } from "@/annotations/use-annotations";
import { circleRadiusLine, circleRing } from "@/annotations/annotation-circle-geometry";
import { circleShowsRadiusLine } from "@/annotations/annotation-label";
import { formatDistance } from "@/units/format-distance";
import type { DistanceUnit } from "@/auth/session-types";

/** Fallback colour for nullable style colours (the draw default). */
const DEFAULT_COLOR = "#f5a623";
/** Outline width boost (px) when selected. */
const SELECT_WIDTH_BOOST = 2;
/** Marker radius boost (px) when selected. */
const SELECT_MARKER_BOOST = 3;
/** Default marker radius (px) — matches the detection marker. */
const DEFAULT_MARKER_SIZE = 6;

/** A dash style + its MapLibre ``line-dasharray`` (null = solid) + line cap.
 *  One MapLibre line layer per entry (dasharray isn't data-driven). Values are
 *  in line-width units; tunable in the §5.11 visual pass. */
export const DASH_LAYERS: ReadonlyArray<{
  readonly dash: string;
  readonly dasharray: readonly number[] | null;
  readonly cap: "butt" | "round";
}> = [
  { dash: "solid", dasharray: null, cap: "butt" },
  { dash: "dashed", dasharray: [2, 2], cap: "butt" },
  { dash: "dotted", dasharray: [0, 2], cap: "round" },
  { dash: "dash_dot", dasharray: [3, 1.5, 0, 1.5], cap: "round" },
];

/** The closed ring coordinates for a circle or polygon annotation (else null). */
function ringFor(annotation: Annotation): number[][] | null {
  const geometry = annotation.geometry;
  if (annotation.kind === "circle" && annotation.radius_m != null && geometry.type === "Point") {
    const coords = geometry.coordinates as number[];
    return circleRing(coords[0], coords[1], annotation.radius_m);
  }
  if (annotation.kind === "polygon" && geometry.type === "Polygon") {
    return (geometry.coordinates as number[][][])[0].map((p) => [...p]);
  }
  return null;
}

/** Fill features (circle + polygon) for the fill layer. */
export function annotationsToFillFeatures(
  annotations: readonly Annotation[],
): FeatureCollection<Polygon> {
  const features: Feature<Polygon>[] = [];
  for (const annotation of annotations) {
    const ring = ringFor(annotation);
    if (ring === null) {
      continue;
    }
    features.push({
      type: "Feature",
      geometry: { type: "Polygon", coordinates: [ring] },
      properties: {
        annotation_id: annotation.annotation_id,
        fill_color: annotation.style.fill_color ?? DEFAULT_COLOR,
        fill_opacity: annotation.style.fill_opacity,
      },
    });
  }
  return { type: "FeatureCollection", features };
}

/** Outline features (line geometry + circle/polygon rings) for the line layers.
 *  Colour/width resolve the selection highlight; ``stroke_dash`` drives the
 *  per-dash layer filter (``null`` → ``"solid"``). */
export function annotationsToOutlineFeatures(
  annotations: readonly Annotation[],
  selectedId: string | null,
): FeatureCollection<LineString> {
  const features: Feature<LineString>[] = [];
  for (const annotation of annotations) {
    const coordinates: number[][] | null =
      annotation.kind === "line" && annotation.geometry.type === "LineString"
        ? (annotation.geometry.coordinates as number[][])
        : ringFor(annotation);
    if (coordinates === null) {
      continue;
    }
    const selected = annotation.annotation_id === selectedId;
    features.push({
      type: "Feature",
      geometry: { type: "LineString", coordinates },
      properties: {
        annotation_id: annotation.annotation_id,
        stroke_color: annotation.style.stroke_color,
        stroke_width: annotation.style.stroke_width_px + (selected ? SELECT_WIDTH_BOOST : 0),
        stroke_dash: annotation.style.stroke_dash ?? "solid",
      },
    });
  }
  return { type: "FeatureCollection", features };
}

/* Arrowhead geometry (ADR-034). The head is a **ground-geometry triangle** (a
 * Polygon, painted by a fill layer) — the high-quality fill the architect chose
 * over the rougher SDF symbol. Its size is a **fixed number of screen pixels**,
 * computed from the **current map zoom** (arrowSizeMetersForZoom) each time the
 * arrow source is rebuilt — which the render layer does on every ``zoom`` event.
 * So the head is the **same size at a given zoom regardless of line length**, and
 * stays a **constant pixel size as you zoom** — like the marker (operator request
 * 2026-06-12; this replaces the prior draw-time-baked ``arrow_size_m``, which made
 * a long line drawn zoomed-out keep an oversized head). It is oriented by the
 * **Mercator** (rendered-segment) bearing, NOT @turf/bearing's great-circle
 * bearing — the latter diverges from the straight Mercator line MapLibre draws on
 * long/steep lines and rotated the head off the line (#2). Capped to the adjoining
 * segment so it never overruns a short line. Tuned in the §5.11 pass. */
const ARROW_HALF_WIDTH_RATIO = 0.55;
const ARROW_SEGMENT_CAP = 0.9;
/** The arrowhead's on-screen length (px) — the fixed pixel size it renders at
 *  every zoom (converted to the equivalent ground metres at the current zoom by
 *  arrowSizeMetersForZoom). Tuned down on operator feedback: 14 → 8 → 6 px. */
export const ARROW_TARGET_PX = 6;
/** Web-Mercator ground metres per screen pixel at zoom 0 on the equator
 *  (earth circumference 40075016.686 m ÷ 256 px/tile). */
const M_PER_PX_ZOOM0 = 156543.03392804097;
/** Metres per degree of latitude (equirectangular approx, as in circle-geometry). */
const METERS_PER_DEG_LAT = 111_320;

/** The ground length (metres) for an arrowhead that should be ``targetPx`` px on
 *  screen at ``zoom`` / ``latDeg`` (Web-Mercator metres-per-pixel × targetPx).
 *  Baked at draw time so the head is a fixed on-screen size when drawn, then
 *  scales with zoom (ADR-034 — "fixed size at the time it is drawn"). */
export function arrowSizeMetersForZoom(targetPx: number, zoom: number, latDeg: number): number {
  const metersPerPixel = (M_PER_PX_ZOOM0 * Math.cos((latDeg * Math.PI) / 180)) / 2 ** zoom;
  return targetPx * metersPerPixel;
}

/** The on-screen bearing (degrees clockwise from north) of the **straight
 *  Web-Mercator segment** MapLibre actually renders between ``a`` and ``b``. The
 *  arrowhead is oriented by this, not @turf/bearing's great-circle bearing —
 *  which diverges from the rendered line on long/steep segments and rotated the
 *  head off the line (#2). Mercator x ∝ lng and y ∝ ln(tan(π/4 + φ/2)) share a
 *  scale, so the screen angle is ``atan2(Δlng, Δmercator-y)``. */
function mercatorBearing(a: number[], b: number[]): number {
  const mercY = (latDeg: number): number =>
    Math.log(Math.tan(Math.PI / 4 + (latDeg * Math.PI) / 360));
  const dEast = ((b[0] - a[0]) * Math.PI) / 180;
  const dNorth = mercY(b[1]) - mercY(a[1]);
  return (Math.atan2(dEast, dNorth) * 180) / Math.PI;
}

/** Planar ground distance in metres between two lng/lat points — accurate at
 *  annotation scale (the inverse of :func:`offsetMeters`). */
function distanceMeters(a: number[], b: number[]): number {
  const latRad = (((a[1] + b[1]) / 2) * Math.PI) / 180;
  const east = (b[0] - a[0]) * METERS_PER_DEG_LAT * Math.cos(latRad);
  const north = (b[1] - a[1]) * METERS_PER_DEG_LAT;
  return Math.hypot(east, north);
}

/** Offset a lng/lat by east/north metres (equirectangular). */
function offsetMeters(lng: number, lat: number, eastM: number, northM: number): number[] {
  const latRad = (lat * Math.PI) / 180;
  return [lng + eastM / (METERS_PER_DEG_LAT * Math.cos(latRad)), lat + northM / METERS_PER_DEG_LAT];
}

/** A filled-triangle arrowhead (``Polygon``) with its tip exactly on ``tip``,
 *  pointing along ``bearingDeg`` (clockwise from north), ``lengthM`` long and
 *  ``2*halfWidthM`` wide in ground metres. Ground geometry → scales with zoom. */
function arrowTriangle(
  id: string,
  tip: number[],
  bearingDeg: number,
  lengthM: number,
  halfWidthM: number,
  color: string,
): Feature<Polygon> {
  const theta = (bearingDeg * Math.PI) / 180;
  const sin = Math.sin(theta);
  const cos = Math.cos(theta);
  // Base corners in a tip-relative east/north metre frame: the head points along
  // +bearing, so the base sits ``lengthM`` behind the tip, ±halfWidth across it.
  const left = offsetMeters(
    tip[0],
    tip[1],
    -lengthM * sin + halfWidthM * cos,
    -lengthM * cos - halfWidthM * sin,
  );
  const right = offsetMeters(
    tip[0],
    tip[1],
    -lengthM * sin - halfWidthM * cos,
    -lengthM * cos + halfWidthM * sin,
  );
  return {
    type: "Feature",
    geometry: { type: "Polygon", coordinates: [[[...tip], left, right, [...tip]]] },
    properties: { annotation_id: id, arrow_color: color },
  };
}

/** Filled-triangle arrowhead Polygons at line endpoints whose ``arrow_start`` /
 *  ``arrow_end`` isn't ``"none"``. The start head points outward (away from the
 *  line); the end head points forward (the travel direction). Lines only. Any
 *  non-"none" value renders the triangle (the open chevron was dropped
 *  2026-06-11; a legacy ``"arrow"`` resolves to a triangle). */
export function annotationsToArrowFeatures(
  annotations: readonly Annotation[],
  zoom: number,
): FeatureCollection<Polygon> {
  const features: Feature<Polygon>[] = [];
  for (const annotation of annotations) {
    if (annotation.kind !== "line" || annotation.geometry.type !== "LineString") {
      continue;
    }
    const coords = annotation.geometry.coordinates as number[][];
    if (coords.length < 2) {
      continue;
    }
    const color = annotation.style.stroke_color;
    const startArrow = annotation.style.arrow_start ?? "none";
    const endArrow = annotation.style.arrow_end ?? "none";
    const id = annotation.annotation_id;
    // Fixed ON-SCREEN size: the ground length for ARROW_TARGET_PX px at the
    // current ``zoom`` + the tip's latitude — so the head is the same size at a
    // given zoom regardless of line length, and a constant pixel size across zoom
    // (like the marker). Capped to the adjoining segment so a short line's head
    // never overruns it. Oriented by the Mercator (rendered-segment) bearing (#2).
    if (startArrow !== "none") {
      const sizeM = arrowSizeMetersForZoom(ARROW_TARGET_PX, zoom, coords[0][1]);
      const len = Math.min(sizeM, distanceMeters(coords[0], coords[1]) * ARROW_SEGMENT_CAP);
      features.push(
        arrowTriangle(
          id,
          coords[0],
          mercatorBearing(coords[1], coords[0]),
          len,
          len * ARROW_HALF_WIDTH_RATIO,
          color,
        ),
      );
    }
    if (endArrow !== "none") {
      const n = coords.length;
      const sizeM = arrowSizeMetersForZoom(ARROW_TARGET_PX, zoom, coords[n - 1][1]);
      const len = Math.min(sizeM, distanceMeters(coords[n - 2], coords[n - 1]) * ARROW_SEGMENT_CAP);
      features.push(
        arrowTriangle(
          id,
          coords[n - 1],
          mercatorBearing(coords[n - 2], coords[n - 1]),
          len,
          len * ARROW_HALF_WIDTH_RATIO,
          color,
        ),
      );
    }
  }
  return { type: "FeatureCollection", features };
}

/** Marker features (points) for the marker layer. */
export function annotationsToMarkerFeatures(
  annotations: readonly Annotation[],
  selectedId: string | null,
): FeatureCollection<Point> {
  const features: Feature<Point>[] = [];
  for (const annotation of annotations) {
    if (annotation.kind !== "marker" || annotation.geometry.type !== "Point") {
      continue;
    }
    const selected = annotation.annotation_id === selectedId;
    features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: annotation.geometry.coordinates as number[] },
      properties: {
        annotation_id: annotation.annotation_id,
        marker_color: annotation.style.marker_color ?? DEFAULT_COLOR,
        marker_size:
          (annotation.style.marker_size_px ?? DEFAULT_MARKER_SIZE) +
          (selected ? SELECT_MARKER_BOOST : 0),
      },
    });
  }
  return { type: "FeatureCollection", features };
}

/**
 * The radius-line source: one two-position ``LineString`` per radius-showing
 * circle, carrying the formatted radius as ``radius_text`` for the symbol layer
 * to place at the line's centre.
 *
 * The label is formatted in the operator's distance unit at build time, so the
 * source is rebuilt when that preference changes (the same treatment the other
 * measurement readouts get).
 */
export function annotationsToRadiusFeatures(
  annotations: readonly Annotation[],
  distanceUnit: DistanceUnit,
): FeatureCollection<LineString> {
  const features: Feature<LineString>[] = [];
  for (const annotation of annotations) {
    if (!circleShowsRadiusLine(annotation) || annotation.geometry.type !== "Point") {
      continue;
    }
    const [lon, lat] = annotation.geometry.coordinates as number[];
    const radiusM = annotation.radius_m as number;
    features.push({
      type: "Feature",
      geometry: { type: "LineString", coordinates: circleRadiusLine(lon, lat, radiusM) },
      properties: {
        annotation_id: annotation.annotation_id,
        stroke_color: annotation.style.stroke_color ?? DEFAULT_COLOR,
        radius_text: formatDistance(radiusM, distanceUnit),
      },
    });
  }
  return { type: "FeatureCollection", features };
}
